/**
 * Calculates allowed text length and returns ready string with ellipsis if needed
 * @param {string} text - source text
 * @param {number} maxWidth - maximum width in pixels (default 280)
 * @param {number} maxLines - maximum number of lines (default 2)
 * @param {number} fontSize - font size in pixels (default 32)
 * @param {number} fontWeight - font weight (default 500)
 * @param {number} lineHeight - line height multiplier (default 1.2)
 * @param {string} fontFamily - font family (default 'Roboto, sans-serif')
 * @returns {string} - ready string for display
 */
export const calculateTruncatedText = (
  text,
  maxWidth = 280,
  maxLines = 2,
  fontSize = 32,
  fontWeight = 500,
  lineHeight = 1.2,
  fontFamily = 'Roboto, sans-serif'
) => {
  if (!text) return '';

  const maxHeight = fontSize * lineHeight * maxLines;
  // Add small tolerance for browser rendering differences
  const heightTolerance = 2;

  // Create temporary element for measurement
  const temp = document.createElement('div');

  temp.style.fontSize = `${fontSize}px`;
  temp.style.fontWeight = fontWeight;
  temp.style.lineHeight = lineHeight;
  temp.style.fontFamily = fontFamily;
  temp.style.position = 'absolute';
  temp.style.visibility = 'hidden';
  temp.style.width = `${maxWidth}px`;
  temp.style.maxWidth = `${maxWidth}px`;
  temp.style.minWidth = `${maxWidth}px`; // Force exact width in Chrome
  temp.style.wordWrap = 'break-word';
  temp.style.wordBreak = 'break-word';
  temp.style.overflowWrap = 'break-word'; // Chrome-specific property
  temp.style.whiteSpace = 'normal';
  temp.style.overflow = 'visible';
  temp.style.boxSizing = 'border-box';
  temp.style.padding = '0';
  temp.style.margin = '0';
  temp.style.border = 'none';
  temp.style.display = 'block'; // Ensure block display
  temp.style.top = '-9999px';
  temp.style.left = '-9999px';
  document.body.appendChild(temp);

  try {
    // Check if full text fits
    temp.textContent = text;

    if (temp.offsetHeight <= maxHeight + heightTolerance) {
      // Text fits completely
      return text;
    }

    // Use binary search to find maximum length
    let low = 0;
    let high = text.length;
    let bestFit = '';
    let iterations = 0;

    while (low <= high && iterations < 20) {
      // Safety limit
      iterations++;
      const mid = Math.floor((low + high) / 2);
      const testText = text.substring(0, mid) + '...';

      temp.textContent = testText;

      if (temp.offsetHeight <= maxHeight + heightTolerance) {
        bestFit = testText;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    // If even minimal text doesn't fit, return at least one character
    return bestFit || (text.length > 0 ? text[0] + '...' : '');
  } finally {
    document.body.removeChild(temp);
  }
};
